/* eslint-disable jsdoc/no-undefined-types */
import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';

const AbstractNotificationsService = MojitoServices.SportsContent.ContentNotifications.AbstractNotificationsService;

const {RequestFactory} = MojitoCore.Services.Transactions;
const logger = MojitoCore.logger.get('DBXNativeXtremePushService');

/**
 * Permission types that can be granted for xtremepush messaging.
 */
const PERMISSIONS = {
    /** User has not allowed or blocked notifications yet. */
    DEFAULT: 'default',
    /** User has already allowed notifications permissions. */
    GRANTED: 'granted',
    /** User has already blocked notifications permissions. */
    DENIED: 'denied',
};

const NOTIFICATIONS_BASE_URL = '/notifications';

/**
 * DBXNativeXtremePushService config.
 *
 * @typedef DBXNativeXtremePushServiceConfig
 * @type {object}
 * @property {string} serviceUrl - URL that will be used to perform API calls.
 */

class DBXNativeXtremePushService extends AbstractNotificationsService {
    constructor(parent) {
        super(parent);
        this.deviceId = undefined;
        this.permissions = PERMISSIONS.DEFAULT;
    }

    /**
     * Configure service.
     * @param {DBXNativeXtremePushServiceConfig} config - Service configuration object.
     * @return {Mojito.Services.SportsContent.ContentNotifications.AbstractNotificationsService} Instance of the service.
     */
    configure(config) {
        const {serviceUrl} = config;
        const self = this;

        this.requestFactory = new RequestFactory(logger);
        this.apiUrl = `${serviceUrl}${NOTIFICATIONS_BASE_URL}`;

        window.Sportsbook.setNativeDeviceId = deviceId => {
            self.deviceId = deviceId;
        };

        window.Sportsbook.setPermissions = permissions => {
            self.permissions = permissions;
        };

        return this;
    }

    /**
     * Activates xtremePush API by forcing user to accept a notifications prompt.
     * Not used in native integration but needs to be defined anyway.
     * @return {Promise} Promise object.
     */
    activate() {
        return Promise.resolve();
    }

    /**
     * Follow request event.
     * @param {Mojito.Services.SportsContent.ContentNotifications.types.FollowEventInfo} payload - Identifier of event that user wants to subscribe.
     * @return {Promise} Event promise.
     */
    followEvent(payload) {
        return this.requestFactory
            .post(`${this.apiUrl}/subscribe`)
            .withCredentials()
            .send(this.getRequestPayload(payload));
    }

    /**
     * Unfollow request event.
     * @param {Mojito.Services.SportsContent.ContentNotifications.types.FollowEventInfo} payload - Unfollow event payload.
     * @return {Promise} Event promise.
     */
    unfollowEvent(payload) {
        return this.requestFactory
            .post(`${this.apiUrl}/unsubscribe`)
            .withCredentials()
            .send(this.getRequestPayload(payload));
    }

    /**
     * Indicates if xtremepush service is available in user's browser.
     * @return {boolean} True if available, false otherwise.
     */
    isAvailable() {
        return this.permissions === PERMISSIONS.GRANTED && this.deviceId;
    }

    /**
     * Indicates if xtremepush service is activated in user's browser.
     * @return {boolean} True if activated, false otherwise.
     */
    isActivated() {
        return true;
    }

    getRequestPayload({notificationsId}) {
        return {
            notificationsId,
            subscriberId: this.deviceId,
        };
    }
}

export default new DBXNativeXtremePushService();
